import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpenCheck, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
export function EditorialPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Editorial Policy & Medical Disclaimer | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-503">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-504">
        <header className="mb-12" data-id="element-505">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-506">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-507" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-508">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-509">
              <BookOpenCheck className="w-8 h-8" data-id="element-510" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-511">
              Editorial Policy
            </h1>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-512">
            How we research, write, and review the nutrition and ingredient
            content published across IntelliEats.
          </p>
          <p className="text-slate-500 text-sm mt-2" data-id="element-513">
            Last updated: July 9, 2026
          </p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-514">
          <section data-id="element-515">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-516">
              Our Editorial Mission
            </h2>
            <p data-id="element-517">
              IntelliEats exists to help everyday shoppers understand what is in
              their packaged food. Every article, guide, and in-app explanation
              is written to be clear, accurate, and genuinely useful — never to
              create fear or promote a specific product. Our goal is awareness,
              not alarm.
            </p>
          </section>

          <section data-id="element-518">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-519">
              How We Research Content
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-520">
              {['We rely on reputable, authoritative sources such as the FDA, USDA, WHO, NIH, CDC, and peer-reviewed nutritional research.', 'Ingredient information reflects the current scientific consensus and clearly distinguishes established facts from areas where evidence is still emerging.', 'When a topic is debated (for example, certain additives or sweeteners), we present the balance of evidence rather than a one-sided claim.', 'We cite our primary references at the end of each article so you can read the source material yourself.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-521">
                  <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-522"></span>
                  <span data-id="element-523">{item}</span>
                </li>)}
            </ul>
          </section>

          <section data-id="element-524">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-525">
              Accuracy & Updates
            </h2>
            <p data-id="element-526">
              Nutrition science evolves. We review our articles periodically and
              update them when guidelines, regulations, or the weight of
              evidence changes. If you spot something that appears inaccurate or
              out of date, please let us know and we will review it promptly.
            </p>
          </section>

          <section data-id="element-527">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-528">
              Independence & Transparency
            </h2>
            <p data-id="element-529">
              Our ingredient assessments are made independently and are not
              influenced by food manufacturers or advertisers. Where a page
              contains advertising, it is clearly part of the page layout and
              does not shape our editorial judgment about any ingredient.
            </p>
          </section>

          <section className="bg-amber-50 rounded-2xl p-8 border border-amber-100" data-id="element-530">
            <div className="flex items-center gap-3 mb-4" data-id="element-531">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-xl" data-id="element-532">
                <Stethoscope className="w-6 h-6" data-id="element-533" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-534">
                Medical Disclaimer
              </h2>
            </div>
            <p className="mb-4" data-id="element-535">
              All content produced by IntelliEats — including articles, guides,
              and the in-app ingredient analysis — is provided for{' '}
              <span className="font-semibold text-slate-900" data-id="element-536">
                educational and informational purposes only
              </span>
              . It is not a substitute for professional medical, nutritional, or
              dietary advice, diagnosis, or treatment.
            </p>
            <p className="mb-4" data-id="element-537">
              Ingredients approved by regulatory authorities are generally
              considered safe when consumed within established limits.
              IntelliEats helps you make informed decisions, but it does not
              diagnose conditions or provide personalized medical guidance.
            </p>
            <p className="m-0" data-id="element-538">
              Always seek the advice of a qualified healthcare professional or
              registered dietitian with any questions you may have regarding a
              medical condition, allergy, or your individual dietary needs.
              Never disregard professional advice because of something you read
              here.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-539">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-540">
              Questions or Corrections
            </h2>
            <p className="mb-4" data-id="element-541">
              If you have questions about our editorial process or would like to
              suggest a correction, reach out anytime:
            </p>
            <p className="font-medium text-slate-900" data-id="element-542">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-543">
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-544">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-545">
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}